import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Star,
  MapPin,
  Phone,
  Globe,
  Clock,
  Heart,
  Bookmark,
  Share2,
  Navigation,
  Camera,
  Users,
  Award,
  CheckCircle,
  ExternalLink,
  Calendar,
  DollarSign,
  Wifi,
  Car,
  Coffee,
  ChevronRight,
  Plus,
  MessageCircle,
  ThumbsUp,
  Eye,
  Filter,
  TrendingUp
} from 'lucide-react';
import { Business, User, Review } from '../types';
import StreetViewComponent from './StreetViewComponent';
import ReviewComponent from './ReviewComponent';

import api, { businessAPI, reviewAPI } from '../services/api';
import SocialShare from './SocialShare';

interface EnhancedBusinessProfileProps {
  business: Business;
  user?: User;
  onClose: () => void;
  onStartReview: () => void;
  onViewFullProfile?: () => void;
  onBookmark?: (businessId: string) => void;
  onFavorite?: (businessId: string) => void;
  isBookmarked?: boolean;
  isFavorited?: boolean;
  positiveMinRating: number;
}

interface BusinessStats {
  totalVisits: number;
  averageWaitTime: string;
  peakHours: string;
  popularItems: string[];
}

const EnhancedBusinessProfile: React.FC<EnhancedBusinessProfileProps> = ({
  business,
  user,
  onClose,
  onStartReview,
  onViewFullProfile,
  onBookmark,
  onFavorite,
  isBookmarked = false,
  isFavorited = false,
  positiveMinRating
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'reviews' | 'photos' | 'streetview'>('overview');
  const [showAllHours, setShowAllHours] = useState(false);
  const [showDirections, setShowDirections] = useState(false);
  const [streetViewAvailable, setStreetViewAvailable] = useState<boolean | null>(null);

  // Sample data - in a real app, this would come from API
  const businessStats: BusinessStats = {
    totalVisits: 1247,
    averageWaitTime: '5-10 min',
    peakHours: '8-10 AM, 12-2 PM',
    popularItems: ['Artisan Latte', 'Avocado Toast', 'Croissant']
  };

  // Social Share
  const [showShareModal, setShowShareModal] = useState(false);

  // fetched review from database
  const [Reviews, setReviews] = useState<any[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const reviewLimit = 1;
  const [reviewsPage, setReviewsPage] = useState<number>(1);
  const [reviewsTotalPages, setReviewsTotalPages] = useState<number>(1);
  const [reviewsTotalReviews, setReviewsTotalReviews] = useState<number>(0);

  const [reviewPhotos, setReviewPhotos] = useState<string[]>([]);
  const [hours, setHours] = useState<
    { day_of_week: string; open_time: string; close_time: string; is_closed: boolean }[]
  >([]);

  // Collapse on scroll
  const [collapsed, setCollapsed] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // fetch business hours
  useEffect(() => {
    const loadHours = async () => {
      try {
        const getBusiness = await businessAPI.getById(business.id) as any;
        if (getBusiness?.hours && Array.isArray(getBusiness.hours)) {
          setHours(getBusiness.hours);
        }
      } catch (err) {
        console.error('Failed to load hours', err);
        setHours([]);
      }
    };
    loadHours();
    fetchPhotos();
    setReviewsPage(1);
  }, [business.id]);

  // fetch reviews
  const fetchReviews = async () => {
    try {
      setLoadingReviews(true);
      const reviews = await reviewAPI.getByBusiness(business.id, positiveMinRating, reviewsPage, reviewLimit);
      setReviews(reviews.data);
      setReviewsTotalPages(reviews.pagination?.totalPages ?? 1);
      setReviewsTotalReviews(
        reviews.totalReviews ?? reviews.totalFilteredReviews ?? reviews.pagination?.total ?? 0
      );

    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviews([]);
      setReviewsTotalPages(1);
      setReviewsTotalReviews(0);
    } finally {
      setLoadingReviews(false);
    }
  };

  // fetch review photos
  const fetchPhotos = async () => {
    try {
      const reviews = await reviewAPI.getByBusiness(business.id, positiveMinRating);
      const photos = reviews.data
        .flatMap((r: any) => r.photos || [])
        .filter((p: string) => !!p);
      setReviewPhotos(photos);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviewPhotos([]);
    } finally {
      // setLoadingReviews(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'reviews') {
      fetchReviews();
    }
    if (activeTab === 'photos') {
      fetchPhotos();
    }
  }, [activeTab, reviewsPage, business.id, positiveMinRating]);

  // Collapse contact info bar on scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => setCollapsed(el.scrollTop > 2);
    handleScroll();
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  const businessHours = hours.reduce((acc, h) => {
    const day = h.day_of_week.charAt(0).toUpperCase() + h.day_of_week.slice(1);
    acc[day] = {
      open: h.is_closed ? 'Closed' : h.open_time.split(':').slice(0, 2).join(':'),
      close: h.is_closed ? 'Closed' : h.close_time.split(':').slice(0, 2).join(':'),
    };
    return acc;
  }, {} as Record<string, { open: string; close: string }>);


  const getCurrentStatus = () => {
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentTime = now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });

    const todayHours = businessHours[currentDay as keyof typeof businessHours];
    if (!todayHours) return { status: 'Unknown', text: 'Hours not available' };

    const openTime = todayHours.open.replace(/[^\d:]/g, '');
    const closeTime = todayHours.close.replace(/[^\d:]/g, '');

    // Simple time comparison (would need more robust logic in real app)
    if (currentTime >= openTime && currentTime <= closeTime) {
      return { status: 'Open', text: `Open until ${todayHours.close}`, color: 'text-green-600' };
    } else {
      return { status: 'Closed', text: `Opens at ${todayHours.open}`, color: 'text-red-600' };
    }
  };

  const status = getCurrentStatus();

  const renderStars = (rating: number, size = 'w-5 h-5') => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`${size} ${i < Math.floor(rating)
          ? 'text-yellow-400 fill-current'
          : 'text-gray-300'
          }`}
      />
    ));
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Status & Quick Actions */}
      <div className="grid grid-cols gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Status</span>
          </div>
          <p className={`text-sm font-semibold ${status.color}`}>{status.text}</p>
        </div>

        {/* <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Users className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Popularity</span>
          </div>
          <p className="text-sm text-gray-700">{businessStats.totalVisits} visits this month</p>
        </div> */}
      </div>

      {/* Description */}
      <div>
        <h4 className="font-medium text-gray-900 mb-2">About</h4>
        <p className="text-gray-700 leading-relaxed">{business.description}</p>
      </div>

      {/* Features & Amenities */}
      {business.features && business.features.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Features & Amenities</h4>
          <div className="grid grid-cols-2 gap-2">
            {business.features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Popular Items/Services */}
      {/* <div>
        <h4 className="font-medium text-gray-900 mb-3">Popular Right Now</h4>
        <div className="space-y-2">
          {businessStats.popularItems.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-900">{item}</span>
              <TrendingUp className="w-4 h-4 text-green-600" />
            </div>
          ))}
        </div>
      </div> */}

      {/* Business Hours */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Hours</h4>
          <button
            onClick={() => setShowAllHours(!showAllHours)}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            {showAllHours ? 'Show less' : 'Show all hours'}
          </button>
        </div>

        <div className="space-y-2">
          {Object.entries(businessHours).slice(0, showAllHours ? 7 : 2).map(([day, hours]) => (
            <div key={day} className="flex justify-between items-center text-sm">
              <span className="font-medium text-gray-900">{day}</span>
              {/* <span className="text-gray-700">{hours.open} - {hours.close}</span> */}
              <span className="text-gray-700">{hours.open} - {hours.close}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Street View Preview */}
      {streetViewAvailable !== false && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Street View Preview</h4>
            <button
              onClick={() => setActiveTab('streetview')}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
            >
              <Navigation className="w-4 h-4" />
              <span>Full View</span>
            </button>
          </div>
          <div className="rounded-lg overflow-hidden">
            <StreetViewComponent
              business={business}
              height="150px"
              onStreetViewLoad={setStreetViewAvailable}
            />
          </div>
        </div>
      )}

      {/* Quick Stats */}
      {/* <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">{businessStats.averageWaitTime}</div>
          <div className="text-sm text-gray-600">Average wait</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">{businessStats.peakHours}</div>
          <div className="text-sm text-gray-600">Peak hours</div>
        </div>
      </div> */}
    </div>
  );

  const renderReviewsTab = () => (
    <div className="space-y-4">
      {/* Reviews Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Customer Reviews</h4>
          <div className="flex items-center space-x-2">
            <div className="flex">{renderStars(business.averageRating || 0)}</div>
            <span className="text-sm text-gray-600">({reviewsTotalReviews} reviews)</span>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2 text-xs">
          {[5, 4, 3, 2, 1].map(rating => (
            <div key={rating} className="flex items-center space-x-1">
              <span>{rating}★</span>
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-400 h-2 rounded-full"
                  style={{ width: `${rating === 5 ? 70 : rating === 4 ? 20 : 5}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Individual Reviews */}
      <div className="space-y-4">
        <ReviewComponent
          reviews={Reviews}
          loading={loadingReviews}
          user={user}
          onHelpfulClick={async (reviewId) => {
            if (!user?.id) return;
            await api.review.addHelpfulVote(reviewId, user.id);
            await fetchReviews();
          }}
        />
      </div>

      {/* Reviews Pagination */}
      {reviewsTotalPages > 1 ? (
        <div className="mt-2 flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={loadingReviews || reviewsPage <= 1}
            onClick={() => setReviewsPage(p => Math.max(1, p - 1))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="text-sm text-gray-600">
            Page {reviewsPage} of {reviewsTotalPages}
          </div>

          <button
            type="button"
            disabled={loadingReviews || reviewsPage >= reviewsTotalPages}
            onClick={() => setReviewsPage(p => Math.min(reviewsTotalPages, p + 1))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );

  // Photos Tab
  const renderPhotosTab = () => (
    <div className="space-y-4">

      {reviewPhotos.length === 0 ? (
        <p className="text-sm text-gray-500">No photos yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {reviewPhotos.map((url, idx) => (
            <div key={idx} className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
              <img key={idx} src={url} alt="Review" className="w-full object-cover rounded-lg" /></div>
          ))}
        </div>
      )}


      {/* <button className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors duration-200">
        <div className="flex items-center justify-center space-x-2">
          <Plus className="w-5 h-5" />
          <span>Add Photos</span>
        </div>
      </button> */}
    </div>
  );

  const renderStreetViewTab = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Virtual Tour</h3>
        <p className="text-sm text-gray-600">
          Explore the area around {business.name} with Google Street View
        </p>
      </div>

      <StreetViewComponent
        business={business}
        height="400px"
        onStreetViewLoad={setStreetViewAvailable}
      />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Navigation className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Street View Tips</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Drag to look around and explore the area</li>
              <li>• Use the controls to zoom and rotate the view</li>
              <li>• Click on street arrows to move along the road</li>
              <li>• Use fullscreen mode for an immersive experience</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const openDirections = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className={`relative ${collapsed ? 'hidden' : ''}`}>
          {/* Cover Image */}
          <div className={`h-48 bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center`}>
            {reviewPhotos.length === 0 ? (
              <Camera className={`w-12 h-12 text-white opacity-50`} />
            ) : (<img src={reviewPhotos[0]} className="w-full h-full object-cover object-center" />)}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute mb-3 top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-opacity duration-200"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Business Info Overlay */}
          <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6 `}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white mb-1">{business.name}</h1>
                <p className={`text-gray-200 text-sm`}>{business.category}</p>
                <div className={`flex items-center space-x-2 mt-2`}>
                  <div className="flex">{renderStars(business.averageRating || 0)}</div>
                  <span className="text-white text-sm">
                    {business.averageRating?.toFixed(1)} ({business.totalReviews} reviews)
                  </span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className={`flex items-center space-x-2`}>
                {/* <button
                  onClick={() => onFavorite?.(business.id)}
                  className={`p-2 rounded-full transition-colors duration-200 ${isFavorited
                    ? 'bg-red-500 text-white'
                    : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                    }`}
                >
                  <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
                </button> */}

                <button
                  onClick={() => onBookmark?.(business.id)}
                  className={`p-2 rounded-full transition-colors duration-200 ${isBookmarked
                    ? 'bg-red-500 text-white'
                    : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                    }`}
                >
                  <Heart className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} />
                </button>

                <button
                  onClick={() => setShowShareModal(true)}
                  className="p-2 bg-white bg-opacity-20 text-white hover:bg-opacity-30 rounded-full transition-colors duration-200">
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Header when scroll */}
        <div className={`relative px-6 py-4 bg-white border-b border-gray-200 ${collapsed ? '' : 'hidden'}`}>
          <h3>{business.name}</h3>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-opacity duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contact Info Bar */}
        <div className={`px-6 py-4 bg-gray-50 border-b border-gray-200 ${collapsed ? 'hidden' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <MapPin className="w-4 h-4" />
                <span>  {business.address === 'Custom location'
                  ? `Custom location`
                  : business.address ?? ''}</span>
              </div>

              {business.phone && (
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${business.phone}`} className="hover:text-primary-600">
                    {business.phone}
                  </a>
                </div>
              )}
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-2">
              <button
                onClick={openDirections}
                className="flex items-center space-x-1 px-3 py-1 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors duration-200"
              >
                <Navigation className="w-4 h-4" />
                <span>Directions</span>
              </button>

              {business.website && (
                <a
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1 px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  <Globe className="w-4 h-4" />
                  <span>Website</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            {[
              { id: 'overview', label: 'Overview', icon: Eye },
              { id: 'reviews', label: 'Reviews', icon: MessageCircle },
              { id: 'photos', label: 'Photos', icon: Camera },
              { id: 'streetview', label: 'Street View', icon: Navigation, disabled: streetViewAvailable === false }
            ].map(({ id, label, icon: Icon, disabled }) => (
              <button
                key={id}
                onClick={() => !disabled && setActiveTab(id as any)}
                disabled={disabled}
                className={`flex-1 flex items-center justify-center space-x-1 py-3 px-1 md:px-4 text-sm font-medium border-b-2 transition-colors duration-200 ${disabled
                  ? 'border-transparent text-gray-300 cursor-not-allowed'
                  : activeTab === id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div ref={scrollRef} className="p-6 max-h-[calc(100vh-25rem)] overflow-y-auto">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'reviews' && renderReviewsTab()}
          {activeTab === 'photos' && renderPhotosTab()}
          {activeTab === 'streetview' && renderStreetViewTab()}
        </div>

        {/* Action Buttons */}
        <div className={`p-6 border-t border-gray-200 bg-gray-50`}>
          <div className="flex space-x-3">
            <button
              onClick={onStartReview}
              className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Star className="w-5 h-5" />
              <span>Write Review</span>
            </button>

            {onViewFullProfile && (
              <button
                onClick={onViewFullProfile}
                className="flex items-center space-x-2 px-4 py-3 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors duration-200"
              >
                <Eye className="w-5 h-5" />
                <span className="hidden sm:inline">Full Profile</span>
              </button>
            )}

            <button className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
              <Calendar className="w-5 h-5" />
            </button>

            <button className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
              <Phone className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Social Share Modal */}
      {showShareModal && (
        <SocialShare
          business={business}
          type="business"
          onClose={() => setShowShareModal(false)}
        />
      )}


    </div>
  );
};

export default EnhancedBusinessProfile;
